import { useShortCodeDataCemeteries } from '@/hooks/use-short-code-data/use-short-code-data'
import { FC } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import {
  distanceFormatter,
  getDirector,
  makeLabels,
  truncateUntilPeriod
} from '@lifedot/utils'
import { router } from '@lifedot/router'
import { Button } from '@lifedot/atoms/Button'
import { typography } from '@lifedot/styles/typography'
import { Label } from '@lifedot/atoms/Label'
import { mq } from '@lifedot/styles/mediaQuery'
import { palette } from '@lifedot/styles/palette'
import { Card } from '@lifedot/atoms/Card'
import { ButtonLabel } from '@lifedot/components/ButtonLabel'
import { replacementImage } from '@lifedot/constants/images'
import { Price } from './Price'
import { css } from '@emotion/react'
import { bestDisplayablePurchasePrices } from '@lifedot/utils'

const styles = {
  card: css({
    display: 'flex',
    [mq('sp')]: {
      flexDirection: 'column-reverse',
      width: '100%'
    }
  }),
  overview: css({
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: 12
  }),
  content: css({
    padding: 12,
    width: 500,
    [mq('sp')]: {
      width: '100%'
    }
  }),
  title: css({
    marginLeft: 12,
    display: 'grid',
    gap: 4,
    width: 'calc(100% - 112px)',
    [mq('sp')]: {
      width: '80%'
    }
  }),
  director: css({
    color: palette.main03,
    display: 'flex',
    alignItems: 'center'
  }),
  directorName: css({
    borderBottom: `1px dotted ${palette.gray6}`,
    fontSize: 0,
    padding: '0 4px 4px'
  }),
  list: css({
    '> li > *': {
      paddingRight: 4
    }
  }),
  comment: css({
    fontWeight: 'bold',
    marginBottom: '0 !important'
  }),
  labels: css({
    marginTop: 8,
    '> *': {
      marginRight: 4,
      marginTop: 4
    }
  }),
  link: css({
    textDecoration: 'none',
    display: 'block',
    marginTop: 16
  }),
  figure: css({
    width: 240,
    display: 'flex',
    [mq('sp')]: {
      width: '100%',
      height: 160
    }
  }),
  image: css({
    objectFit: 'cover'
  })
}

interface DirectorCemeteryCardProps {
  cemeteryId: number
}

export const DirectorCemeteryCard: FC<DirectorCemeteryCardProps> = ({
  cemeteryId
}) => {
  const cemeteryData = useShortCodeDataCemeteries([cemeteryId])[0]

  if (!cemeteryData) return null

  const {
    name: cemeteryName,
    city: { name: cityName },
    nearest_station: station,
    cemetery_types,
    director_comment: directorComment,
    features,
    facilities,
    requestable,
    main_image: mainImage
  } = cemeteryData

  const { iconImagePath, nameImagePath } = getDirector(cemeteryId)
  const price = bestDisplayablePurchasePrices(
    cemeteryData.best_price_by_category
  )

  return (
    <Card shadow round={1}>
      <div css={styles.card}>
        <div css={styles.content}>
          <div css={styles.overview}>
            <Image
              src={iconImagePath}
              width={100}
              height={100}
              alt="お墓ディレクターの画像"
            />
            <div css={styles.title}>
              <div css={styles.director}>
                <div css={styles.directorName}>
                  <Image
                    src={nameImagePath}
                    width={75}
                    height={25}
                    alt="お墓ディレクター"
                  />
                </div>
                <span css={typography.textM}>が薦める</span>
              </div>
              <Link
                href={router.cemetery(cemeteryId)}
                passHref
                prefetch={false}
              >
                <a>
                  <h3 css={typography.headingS}>{cemeteryName}</h3>
                </a>
              </Link>
              <ul css={styles.list}>
                <li css={typography.textS}>
                  <span>{cityName}</span>
                  {!!station.name && !!station.distance && (
                    <span>
                      / {station.name}駅から
                      {distanceFormatter(station.distance)}
                    </span>
                  )}
                </li>
                <li css={typography.textS}>
                  <span>
                    {cemetery_types
                      .filter(({ name }) => name !== '公営以外')
                      .map(({ name }) => name)}
                  </span>
                  <Price price={price} />
                </li>
              </ul>
            </div>
          </div>
          {directorComment && (
            <p css={styles.comment}>
              {truncateUntilPeriod(directorComment)}…
              <Link href={router.cemetery(cemeteryId)} prefetch={false}>
                続きを読む
              </Link>
            </p>
          )}
          <div css={styles.labels}>
            {makeLabels([...features, ...facilities]).map(({ code, name }) => (
              <Label large emphasis key={code}>
                {name}
              </Label>
            ))}
          </div>
          {requestable && (
            <Link
              href={router.requestsNew({ id: cemeteryId })}
              passHref
              prefetch={false}
            >
              <a css={styles.link}>
                <Button
                  mainText={
                    <>
                      <span css={typography.textL}>
                        パンフレット請求・見学予約する
                      </span>
                      <ButtonLabel>無料</ButtonLabel>
                    </>
                  }
                  color="accent"
                  as="div"
                />
              </a>
            </Link>
          )}
        </div>
        <Link href={router.cemetery(cemeteryId)} passHref prefetch={false}>
          <a css={styles.figure}>
            <Image
              src={mainImage?.path ?? replacementImage.path}
              alt={mainImage?.alt ?? cemeteryName}
              width={1037}
              height={741}
              css={styles.image}
            />
          </a>
        </Link>
      </div>
    </Card>
  )
}
